import type {
  MLKEMKeyPair,
  PasswordHashResult,
} from '@/services/abstract/poc/crypto.service.interface';
import type { PocServices } from '@/types/services.types';

/** Everything derived from a user's password: UKEK, ML-KEM keypair, and the encrypted private key. */
export interface UserKeyMaterial {
  ukekHex: string;
  hashResult: PasswordHashResult;
  keyPair: MLKEMKeyPair;
  encryptedPrivateKeyHex: string;
}

/**
 * Derives the full per-user key material from credentials. Shared by the
 * register, recover, and change-password flows — the sequence must stay
 * identical across all three or rotated keys stop matching login-derived ones.
 *
 * The slow Argon2 hash runs exactly once: `deriveKey` is `hashPassword().keyHex`
 * and the ML-KEM seed is built from the same hash, so both are derived from the
 * single `hashResult` rather than re-hashing per output.
 */
export async function deriveUserKeyMaterial(
  cryptoService: PocServices['cryptoService'],
  username: string,
  password: string
): Promise<UserKeyMaterial> {
  const hashResult = await cryptoService.hashPassword(password, username);
  const ukekHex = hashResult.keyHex;
  const keyPair = await cryptoService.generateMlKemKeyPairFromPasswordHash(hashResult);
  const encryptedPrivateKeyHex = await cryptoService.encryptPrivateKey(
    keyPair.privateKey,
    hashResult
  );
  return { ukekHex, hashResult, keyPair, encryptedPrivateKeyHex };
}
